import { AGE_BANDS, type AgeBand, type ReviewedRow } from "@/types";

/**
 * The review grid's rules.
 *
 * Pure and separate from the grid, because the distinction that matters is not
 * visual. A **blocking** problem stops the whole import; a **warning** does
 * not. Getting them the wrong way round either creates thirty accounts nobody
 * checked, or refuses an import over two people with the same name — which
 * happens in every school of any size.
 */

export type ProblemSeverity = "blocking" | "warning";

export interface RowProblem {
  severity: ProblemSeverity;
  /** i18n key. */
  key: string;
  params?: Record<string, unknown>;
}

const MAX_NAME_LENGTH = 80;
const MAX_GROUP_NAME_LENGTH = 60;

/**
 * Whether this row would create a group, and therefore needs an age band.
 *
 * A new group cannot be created without one: the age band decides whether
 * parental consent is required, and a group created without it would be a group
 * that quietly needs no consent. An existing group already has one.
 */
export function needsAgeBand(row: ReviewedRow, existingGroups: ReadonlySet<string>): boolean {
  if (row.role !== "student") return false;
  if (!row.groupName) return false;
  return !existingGroups.has(row.groupName.trim().toLowerCase());
}

export function problemsFor(
  row: ReviewedRow,
  context: {
    /** Lower-cased names of groups that already exist. */
    existingGroups: ReadonlySet<string>;
    /** How many rows in this import share this name within the same group. */
    duplicateCount: number;
  }
): RowProblem[] {
  const problems: RowProblem[] = [];
  const name = row.displayName.trim();

  if (name.length === 0) {
    problems.push({ severity: "blocking", key: "import.problem.noName" });
  } else if (name.length > MAX_NAME_LENGTH) {
    problems.push({
      severity: "blocking",
      key: "import.problem.nameTooLong",
      params: { max: MAX_NAME_LENGTH },
    });
  }

  if (row.groupName !== undefined && row.groupName.trim().length > MAX_GROUP_NAME_LENGTH) {
    problems.push({
      severity: "blocking",
      key: "import.problem.groupNameTooLong",
      params: { max: MAX_GROUP_NAME_LENGTH },
    });
  }

  if (row.ageBand !== undefined && !AGE_BANDS.includes(row.ageBand)) {
    problems.push({ severity: "blocking", key: "import.problem.invalidAgeBand" });
  }

  if (row.ageBand === undefined && needsAgeBand(row, context.existingGroups)) {
    problems.push({
      severity: "blocking",
      key: "import.problem.ageBandRequired",
      params: { group: row.groupName ?? "" },
    });
  }

  /*
    A warning, deliberately. Two people called Anna Berger in one class is
    ordinary in a school of any size, and the usernames they are issued differ.
    Refusing the import would be refusing reality; saying nothing would let a
    genuinely duplicated row through unnoticed.
  */
  if (context.duplicateCount > 1) {
    problems.push({
      severity: "warning",
      key: "import.problem.duplicateInGroup",
      params: { count: context.duplicateCount },
    });
  }

  return problems;
}

export interface ReviewSummary {
  ready: number;
  needAttention: number;
  excluded: number;
  /** True when nothing blocks. Commit is disabled otherwise. */
  canCommit: boolean;
  problems: Map<number, RowProblem[]>;
}

function duplicateKey(row: ReviewedRow): string {
  return `${row.displayName.trim().toLowerCase()}::${(row.groupName ?? "").trim().toLowerCase()}`;
}

export function summarise(
  rows: readonly ReviewedRow[],
  excluded: ReadonlySet<number>,
  existingGroups: ReadonlySet<string>
): ReviewSummary {
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (excluded.has(row.rowNumber)) continue;
    const key = duplicateKey(row);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const problems = new Map<number, RowProblem[]>();
  let ready = 0;
  let needAttention = 0;

  for (const row of rows) {
    if (excluded.has(row.rowNumber)) continue;

    const found = problemsFor(row, {
      existingGroups,
      duplicateCount: counts.get(duplicateKey(row)) ?? 1,
    });
    if (found.length > 0) problems.set(row.rowNumber, found);

    if (found.some((problem) => problem.severity === "blocking")) needAttention += 1;
    else ready += 1;
  }

  const canCommit =
    ready + needAttention > 0 &&
    ![...problems.values()].some((list) =>
      list.some((problem) => problem.severity === "blocking")
    );

  return { ready, needAttention, excluded: excluded.size, canCommit, problems };
}

/** The bands a person can be put in, in the order a teacher reads them. */
export const SELECTABLE_AGE_BANDS: readonly AgeBand[] = ["u13", "13-15", "16-17", "adult"] as const;
